import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { DynamicIcon, type IconName } from 'lucide-react/dynamic'
import type { Template } from './TemplateCard'

interface TemplatePreviewModalProps {
  template: Template | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onUseTemplate: (template: Template) => void
  isCreating?: boolean
}

export function TemplatePreviewModal({
  template,
  open,
  onOpenChange,
  onUseTemplate,
  isCreating = false,
}: TemplatePreviewModalProps) {
  const { t } = useTranslation('projects')

  if (!template) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
        {/* Header with title, description and Use Template button */}
        <div className="flex flex-col gap-1.5">
          <DialogTitle className="text-xl pr-10">{template.name}</DialogTitle>
          <div className="flex items-center justify-between gap-4">
            <DialogDescription className="flex-1">{template.description}</DialogDescription>
            <Button
              onClick={() => onUseTemplate(template)}
              disabled={isCreating}
              className="shrink-0"
            >
              {isCreating ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                  {t('templateBrowser.creating')}
                </>
              ) : (
                t('templateBrowser.useTemplate')
              )}
            </Button>
          </div>
        </div>

        {/* Screenshot preview or placeholder */}
        <div className="flex-1 rounded-lg overflow-y-auto border bg-muted/30 min-h-0">
          {template.screenshot_url ? (
            <img
              src={template.screenshot_url}
              alt={`${template.name} Preview`}
              className="w-full h-auto"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <DynamicIcon name={template.icon as IconName} className="h-16 w-16 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">{t('templateBrowser.noPreview')}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
